import React from 'react';
import ReactDOM from 'react-dom';
import { TrendingUp, TrendingDown, X, Unplug } from 'lucide-react';
import { variationColor } from '../../utils/variationUtils';
import { destinationAlerte, titreAlerte, corpsAlerte, valeurSaillante, estAlerteAcces } from './messageAlerte';

/** Au-delà, les cartes couvriraient l'écran : un balayage peut en ouvrir dix d'un coup. */
const CARTES_VISIBLES = 3;

/**
 * Les alertes nouvelles, en cartes empilées en bas à droite.
 *
 * C'est le visage in-app du canal « navigateur » : quand l'onglet est regardé, une carte dans
 * la page dit la même chose qu'une bulle système, sans rien demander à personne. La bulle ne
 * garde que le cas où l'onglet ne l'est pas — voir l'aiguillage dans `AlertNotificationsBell`.
 *
 * **Portalisée**, comme les modales du projet : le panneau de la cloche est un
 * `position: absolute` prisonnier de l'en-tête, et une carte doit flotter au-dessus de tout,
 * y compris d'une modale ouverte (`zIndex` au-delà du 9999 des modales).
 *
 * **Persistante** : une alerte de budget qui s'évapore pendant qu'on regarde ailleurs n'a pas
 * rempli son office. Elle ne part qu'à la fermeture explicite.
 *
 * **Cliquable**, comme la ligne correspondante sous la cloche, et vers la même destination :
 * la carte dit la même chose que la ligne du panneau, elle doit mener au même endroit. La règle
 * vit dans `messageAlerte.js` plutôt qu'ici, pour ne pas diverger au premier ajustement.
 */
const PopinAlertes = ({ alertes, onFermer, onToutVoir, onOuvrir }) => {
  if (!alertes || alertes.length === 0) return null;

  const visibles = alertes.slice(0, CARTES_VISIBLES);
  const reste = alertes.length - visibles.length;

  return ReactDOM.createPortal(
    <div style={{
      position: 'fixed', bottom: '24px', right: '24px', zIndex: 10000,
      display: 'flex', flexDirection: 'column', gap: '10px',
      width: '360px', maxWidth: 'calc(100vw - 32px)'
    }}>
      {visibles.map(alerte => {
        const acces = estAlerteAcces(alerte);
        // Une hausse de dépense ou de clics est mauvaise : sémantique inversée. Un accès
        // coupé n'a pas de sens de variation — il est rouge, sans nuance.
        const couleur = acces ? '#ef4444' : variationColor(alerte.deviationPercent, true);
        const Icone = acces ? Unplug : (alerte.direction === 'up' ? TrendingUp : TrendingDown);

        const { dashboard, ouvrable: meneQuelquePart, titre } = destinationAlerte(alerte);
        const ouvrable = Boolean(onOuvrir && meneQuelquePart);

        return (
          <div
            key={alerte.id}
            onClick={ouvrable ? () => onOuvrir(alerte, dashboard) : undefined}
            title={ouvrable ? titre : undefined}
            onMouseEnter={e => { if (ouvrable) e.currentTarget.style.backgroundColor = '#f9fafb'; }}
            onMouseLeave={e => { if (ouvrable) e.currentTarget.style.backgroundColor = 'white'; }}
            style={{
              background: 'white', border: '1px solid #e5e7eb', borderLeft: `3px solid ${couleur}`,
              borderRadius: '8px', boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
              padding: '12px 14px', animation: 'slideUp 0.3s ease',
              cursor: ouvrable ? 'pointer' : 'default',
              transition: 'background-color 0.12s ease'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
              <Icone size={16} style={{ color: couleur, flexShrink: 0, marginTop: '2px' }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontSize: '13px', fontWeight: 600, color: '#111827',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                }}>
                  {titreAlerte(alerte)}
                </div>
                <div style={{ fontSize: '12px', color: '#4b5563', marginTop: '4px', lineHeight: 1.45 }}>
                  {corpsAlerte(alerte)}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                <span style={{
                  fontSize: acces ? '11px' : '13px', fontWeight: 700, color: couleur,
                  whiteSpace: 'nowrap'
                }}>
                  {valeurSaillante(alerte)}
                </span>
                <button
                  type="button"
                  /* `stopPropagation` : sans lui, fermer la carte l'ouvrirait aussi —
                     le clic remonterait jusqu'à la carte devenue cliquable. */
                  onClick={(e) => { e.stopPropagation(); onFermer(alerte.id); }}
                  title="Fermer"
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    color: '#9ca3af', display: 'flex', padding: '2px'
                  }}
                >
                  <X size={14} />
                </button>
              </div>
            </div>
          </div>
        );
      })}

      {/* Les cartes au-delà du plafond ne sont pas perdues : la cloche les liste toutes. */}
      {reste > 0 && (
        <button
          type="button"
          onClick={onToutVoir}
          style={{
            background: 'white', border: '1px solid #e5e7eb', borderRadius: '8px',
            boxShadow: '0 10px 25px rgba(0,0,0,0.15)', padding: '10px 14px',
            fontSize: '12px', fontWeight: 600, color: '#374151', cursor: 'pointer',
            textAlign: 'center', animation: 'slideUp 0.3s ease'
          }}
        >
          + {reste} autre{reste > 1 ? 's' : ''} alerte{reste > 1 ? 's' : ''} — tout voir
        </button>
      )}
    </div>,
    document.body
  );
};

export default PopinAlertes;
